import React, { useEffect, useState } from 'react';
import { Button, Modal, Spin, Input, Tooltip, Progress } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import {downloadEngineeringDocFile, getDocumentsList} from '../../../../../../../services/documentService';
import { message } from "antd";
import { logger } from '../../../../../../../services/loggingService';
import { useViewerStore } from "../../../../../../../store/store";
import {Download, FileText} from "@phosphor-icons/react";
import {useAreChangesMadeToDesign} from "../../../../../../../hooks/useAreChangesMadeToDesign";

export interface EngineeringDocument {
    Id: number;
    FileName: string;
    FileType: string;
    Description: string;
    Conga_Template_Id: string;
    Last_Modified_Date: string; // ISO 8601 timestamp
    File_Description: string;
}

export type DownloadStatusType = "success" | "active" | "normal" | "exception" | undefined


export default function EngineeringDocsButton() {

    const { areChangesMade, isOriginalSite } = useAreChangesMadeToDesign();
    const [messageApi, contextHolder] = message.useMessage();
    const {designList} = useViewerStore(state => state);

    const [isModal, setIsModal] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isDownLoading, setIsDownLoading] = useState<boolean>(false);
    const [docs, setDocs] = useState<EngineeringDocument[]>([]);
    const [filteredDocs, setFilteredDocs] = useState<EngineeringDocument[] | null>(null);
    const [selItems, setSelItems] = useState<EngineeringDocument[]>([]);
    const [searchText, setSearchText] = useState<string>("");
    const [downloadingFile, setDownloadingFile] = useState<string | null>(null);
    const [downloadProgress, setDownloadProgress] = useState<number>(0);
    const [downloadStatus, setDownloadStatus] = useState<DownloadStatusType>(undefined);



    useEffect(() => {
        useViewerStore.setState({ isModalOpenInInterface: isModal });
        setDownloadingFile(null);
    }, [isModal]);

    useEffect(() => {
        if (searchText == null || searchText.trim() == "") {
            setFilteredDocs(docs);
            return;
        }

        const filtered = docs.filter((doc) => {
            const searchTextNormalized = searchText.trim().toLowerCase();
            const filNameNormalized = doc.FileName.toLowerCase();
            const descriptionNormalized = doc.Description?.toLowerCase() ?? "";

            const isSearchByTerms = searchText.includes(" ");
            if (!isSearchByTerms) {
                return filNameNormalized.includes(searchTextNormalized) ||
                       descriptionNormalized.includes(searchTextNormalized) ||
                       doc.FileType.toLowerCase().includes(searchTextNormalized);
            }

            const terms = searchTextNormalized
                .split(" ")
                .map((term) => term.trim());

            return terms.every((term) =>
                filNameNormalized.includes(term) ||
                descriptionNormalized.includes(term));
        });

        if (filtered.length > 0) {
            setFilteredDocs(filtered);
        } else {
            setFilteredDocs(null);
        }
    }, [searchText]);

    const openEngineeringDocs = async () => {
        setSelItems([])
        setIsModal(true);
        setIsLoading(true);
        try {
            const docs = await getDocumentsList('Engineering Documents');
            setDocs(docs);
            setFilteredDocs(docs);
            setIsLoading(false);
        } catch (error) {
            logger.error(error);
            setIsLoading(false);
        }
    }

    const docSelectHandler = (doc : EngineeringDocument) => {
        setDownloadingFile(null);
        const isSelected = selItems.some(item => item.Id == doc.Id);
        if (isSelected) {
            setSelItems(selItems.filter(item => item.Id !== doc.Id));
        } else {
            setSelItems([...selItems, doc]);
        }
    }

    const downloadHandler = async () => {
        if (designList.length === 0) {
            alertMessage("error", "Site must be finalized prior to downloading engineering documents");
        } else {
            setIsDownLoading(true)
            const salesForceId = sessionStorage.getItem('SalesForceId');
            try {
                selItems.forEach(async doc => {
                    const {FileName, FileType, Last_Modified_Date, Description, Conga_Template_Id} = doc;
                    const curFileName = `${FileName}${FileType}`;
                    const payload = { fileName: curFileName, salesForceId, last_Modified_Date: Last_Modified_Date, file_Description: Description, conga_Template_Id: Conga_Template_Id };
                    setDownloadingFile(curFileName);
                    setDownloadProgress(0);
                    setDownloadStatus('active');
                    await downloadEngineeringDocFile(payload,
                        (fileName : string, progress : number)=>{
                            setDownloadingFile(fileName);
                            setDownloadProgress(progress);
                            setDownloadStatus('active');
                        }).then((response) => {
                            if (response && response?.status === 200) {
                                const blob = new Blob([response.data], { type: response.headers['content-type'] });
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', curFileName);
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                                setDownloadProgress(100);
                                setDownloadStatus('success');
                            }
                            else {
                                setDownloadStatus('exception');
                                alertMessage("error", `Something went wrong while downloading the ${curFileName} file. Please try again`);
                            }
                        })
                        .catch((error) => {
                            setDownloadStatus('exception');
                            logger.error(error);
                            alertMessage("error", `Something went wrong while downloading the ${curFileName} file. Please try again`);
                        });
                });
            } catch (error) {
                logger.error(error);
                alertMessage("error", "Something went wrong while downloading the files. Please try again");
            }
            setIsDownLoading(false)
        }
    }

    const alertMessage = (type : string, content : any) => {
        messageApi.open({
          content: content,
          duration : 4,
          className:
            type === "error"
              ? "alertError"
              : type === "success"
              ? "alertSuccess"
              : "",
        });
    };


    return (
        <>
        {contextHolder}
        <Tooltip title={areChangesMade ? "Changes were made. Finalize before downloading."
                            : isOriginalSite
                            ? "Feature disabled for the Original design. Select a finalized design."
                            : ""}>
            <Button
                disabled={areChangesMade || isOriginalSite}
                icon={<Download size={20}/>}
                type='primary' size='large' block onClick={openEngineeringDocs}>Engineering Documents</Button>
        </Tooltip>
            <Modal
                open={isModal}
                onOk={() => setIsModal(false)}
                onCancel={() => setIsModal(false)}
                title={[
                    <div style={{ display: "flex", justifyContent: "flex-end", paddingRight: '10px' }}>
                        <Input
                            ref={input => input && input.focus()}
                            allowClear
                            placeholder="Search..."
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ width: 300 }} />
                    </div>
                ]}
                footer={[
                    <div style={{display: "flex", justifyContent: "flex-end"}}>
                        {downloadingFile &&
                            <div style={{ paddingRight: "20px", justifyContent: "flex-end" }}>
                                <span>
                                    {downloadStatus === 'exception' ? 'Failed: '
                                        : downloadStatus === 'success' ? 'Downloaded: ' : 'Downloading: '}
                                </span>
                                <span>
                                    {downloadingFile}
                                </span>
                                <Progress percent={downloadProgress} status={downloadStatus} />
                            </div>}
                        <Button key="download" type="primary"
                                loading={isDownLoading}
                                onClick={() => downloadHandler()}
                                disabled={selItems.length === 0}
                        >
                            Download
                        </Button>
                    </div>
                ]}
                width={900}
                styles={{ body: { overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' } }}
            >
                <table className={"tab__table"}>
                    <thead>
                    <tr>
                        <th align="center">
                            Select
                        </th>
                        <th align="center">Description</th>
                        <th align="center">Document</th>
                        <th align="center">Type</th>
                    </tr>
                    </thead>

                    <tbody>
                    {isLoading ? (
                        <tr>
                            <td colSpan={3} align="center">
                                <Spin className="doctable_spin"/>
                            </td>
                        </tr>
                    ) : filteredDocs ? (
                        filteredDocs.map((doc, index : number) => (
                            <tr key={index}>
                                <td align="center">
                                    <input
                                        type="checkbox"
                                        checked={selItems.some(item => item.Id == doc.Id)}
                                        onChange={() => docSelectHandler(doc)}
                                    />
                                </td>
                                <td align="left">{doc.Description}</td>
                                <td align="left">{doc.FileName}</td>
                                <td align="center">{doc.FileType}</td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={3} align="center">
                                No documents found
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>

            </Modal>
        </>
    );
}
